import { browserInstance as browser } from '@/utils/browser';
import {
  LIVE_SALESFORCE_PATH,
  LIVE_SALESFORCE_PATH_NEW,
  SALESFORCE_CS_CHAT_VERSION,
  AGENT_CHAT_VERSION,
  LIVE_FROM_PAGE,
  LIVE_SAVE_HISTORY_KEY,
} from './constants';
import { COUNTRY } from './locale';
import { getTrans, bridgeCallFunc } from './instantLoaded/tools';
import { addTip } from './sessionUtils';
import initStore from '@/stores/init';
import sessionStore from '@/stores/session';
import userStore from '@/stores/user';
import { joinChat } from './agent';
import { UserType } from '@shopee/shopee-chatbot-agent-chat/dist/types';
import { redirectTrack } from '@/utils/tracking/utils/redirectTrack';
import { UseLiveChatSDKApp } from '@/config/core';
import { isFood } from '@/utils/saas';
import { generateOfflineFormUrl } from './tools';

const getSalesforcePath = (isNew) => {
  const params = {
    offlineFormUrl: encodeURIComponent(generateOfflineFormUrl()),
    fromPage: LIVE_FROM_PAGE,
    chatbotTask: sessionStore.activatedDialogueType.join(',') || '',
    chatbotArticle: sessionStore.showedArticleList.join(',') || '',
    chatbotSessionId: initStore.sessionId || '',
    orderSN: sessionStore.orderSNList.join(',') || '',
    tierInfo: userStore.loyaltyTierCode,
  };

  if (ENVIRONMENT === 'development' || ENVIRONMENT === 'test') {
    params.offlineFormUrl = 'https%3A%2F%2Ffullcopy-shopee-help.cs113.force.com%2Fs%2Fcontactusform';
    params.buttonID = '5731s0000004CEX';
    params.orgID = '00D1s0000008adM';
    params.deploymentID = '5721s0000008OMl';
    params.podURL = 'd.la2-c1cs-hnd.salesforceliveagent.com';
  }

  let pathParams = '';
  for (const key in params) {
    if (params[key]) {
      pathParams += (pathParams ? '&' : '') + key + '=' + params[key];
    }
  }
  return (isNew ? LIVE_SALESFORCE_PATH_NEW : LIVE_SALESFORCE_PATH) + pathParams;
};

const generateContent = {
  Msg: (data) => {
    return data?.content || '';
  },
  HotQuestions: () => {
    return 'Initial hot questions';
  },
  SingleArticle: (data) => {
    return `[Article]${data.article.title}`;
  },
  MultiArticle: (data) => {
    return data.msgContent;
  },
  QAButton: (data) => {
    return data.msgContent;
  },
  SelectOrder: (data) => {
    return data.msgContent;
  },
  Options: (data) => {
    return data.msgContent;
  },
  Feedback: (data) => {
    return data.msgContent;
  },
  Redirection: (data) => {
    return data.msgContent;
  },
  Live: (data) => {
    return data.msgContent;
  },
  OrderList: () => {
    return 'Actived order list';
  },
  Order: (data) => {
    return data.title;
  },
  OrderCardWithHQ: (data) => {
    return data?.order?.title;
  },
  Login: (data) => {
    return data.msgContent;
  },
  EnquiryDetails: () => {
    return getTrans(isFood() ? 'shopee_foodVN_enquiry_details_title' : 'enquiry_details_title') + ' ' + getTrans('enquiry_details_desc');
  },
  General: (data) => {
    return data.msgContent;
  },
  NegativeOptions: (data) => {
    return data.msgContent;
  },
  StateTransactionList: () => {
    return 'Actived transaction list';
  },
  Transaction: (data) => {
    return data.title;
  },
  Announcements: () => {
    return 'Initial announcements';
  },
  IntentSelector: () => {
    return 'Intent selector';
  },
  OrderCardWithSN: data => data?.order?.title,
  OrderCardWithButton: data => data?.order?.title,
  OrderWithConfirmation: data => data?.title,
  QuestionsList: (data) => data.msgContent,
  NewHotQuestion: data => data.msgContent,
  IntentHotQuestion: () => 'Initial 3x3 hot questions',
  HotQuestionCategory: () => 'Initial hot questions category',
  SPLater: data => data.title,
  MySupportCase: () => 'My Support Case',
  Image: (data) => `image: ${data.imgUrl}`,
  SelectOrder: (data) => data.msgContent,
  TFEMessage: (data) => data.content || '',
  TFEOrderList: () => 'Actived order list',
  TFETransactionList: () => 'Actived transaction list',
  TFERelatedIntent: (data) => data.content || '',
  TFEModal: (data) => data.title || '',
  TFEOptionSelector: (data) => data.content || '',
  TFEArticle: (data) => `[Article]${data?.title}`,
  TFEOrder: (data) => data.title || '',
  TFETransaction: (data) => data.title || '',
  TFECancelOrder: (data) => data.modal?.title || 'Cancel this order',
  TFEGuaranteeModal: (data) => data.modalProps?.title || 'Extend Shopee Guarantee',
};

// 作为参数传给salesforce的历史消息
export const getHistoryForSalesforceParams = () => {
  const dialogueList = sessionStore.dialogueList;
  const allHistory = [];
  dialogueList.forEach((dialogue) => {
    if (dialogue.from !== 'system') {
      const func = generateContent[dialogue.type];
      const content = (func && func(dialogue.data)) || '';
      allHistory.push(dialogue.from + ': ' + content);
    }
  });
  return allHistory.join('\n');
};

export const getHistoryForInhouseParams = () => {
  const dialogueList = sessionStore.dialogueList;
  const cbHistory = [];
  dialogueList.forEach((dialogue) => {
    if (dialogue.from !== 'system') {
      const func = generateContent[dialogue.type];
      const content = (func && func(dialogue.data)) || '';
      const map = {
        bot: UserType.BOT,
        user: UserType.Buyer,
      };
      cbHistory.push({
        message_id: dialogue?.data?.id,
        content,
        sender_type: map[dialogue.from],
        send_time: dialogue.ctime,
      });
    }
  });
  return cbHistory;
};

// old logic
export const toSDKLiveAgent = () => {
  try {
    bridgeCallFunc(() => {
      window.bridgeCallHandler(
        'save',
        {
          key: LIVE_SAVE_HISTORY_KEY,
          data: getHistoryForSalesforceParams(),
          persist: 0,
        },
        () => {
          // 等save返回了后再掉起live
          const isApp = browser.shopeeAppOrLiveChat;
          const version = window.getAppVersion();

          const isNewPath = version > SALESFORCE_CS_CHAT_VERSION;
          const path = getSalesforcePath(isNewPath);
          // 浏览器环境只输出 log, 不触发 sdk 跳转
          if (!isApp) {
            console.log(path);
            return;
          }

          redirectTrack({ url: path });
          window.bridgeCallHandler('navigateAppPath', {
            path,
            params: isNewPath ? { __pc__: 1 } : {},
          });

          // 转人工后清除当前sessionId
          initStore.UPDATE_SESSIONID();
        },
      );
    });
  } catch (err) {
    addTip(getTrans('error_agent_fail'));
  }
};

export function shouldGoSDK() {
  if (UseLiveChatSDKApp.is()) {
    return false;
  }
  if (!initStore.liveAgentChatVersion) {
    return true;
  }
  // br 地区安卓没有打包 floating bubble 相关的 bridge, 这里通过配置读取需要兼容的安卓版本
  const version = window.getAppVersion();
  if (COUNTRY === 'br' && browser.android && version < initStore.appPolyfillVersion) {
    return true;
  }
  if (version <= AGENT_CHAT_VERSION) {
    return true;
  }
}

export default async (props = {}) => {
  // 已经不会走到这里了!
  if (shouldGoSDK()) {
    // 老的跳转逻辑
    toSDKLiveAgent();
    return;
  }

  try {
    // 唤起live agent逻辑
    await joinChat(props);
  } catch (err) {
    console.error(err);
    addTip(getTrans('error_agent_fail'));
  }
};
